/**
 * Firstedge Sports Quiz V2 Engine
 * Framework: Vue 3 (Global window mount)
 * Features: Tag-based dynamic matching, AJAX Add to Cart, AJAX Lead Form
 */

document.addEventListener("DOMContentLoaded", () => {
  // Wait until Vue is available and steps are loaded
  const checkInterval = setInterval(() => {
    if (window.Vue && window.gamePlay) {
      clearInterval(checkInterval);
      initQuizV2();
    }
  }, 50);

  function initQuizV2() {
    const { createApp, ref, computed, onMounted } = window.Vue;

    createApp({
      setup() {
        // App State
        const currentScreen = ref("quiz"); // quiz, lead_capture, recommendations
        const currentQuestionId = ref(1);
        const selectedAnswer = ref(null);
        const history = ref([]);
        const isSubmitting = ref(false);
        const addingToCartId = ref(null);

        // Answers Object (as requested)
        const answers = ref({
          sport: "",
          experience: "",
          level: "",
          goal: "",
          gameplay: ""
        });

        // Lead Form State
        const leadForm = ref({
          name: "",
          email: "",
          phone: "",
          countryCode: "+91"
        });

        const errors = ref({
          name: "",
          email: "",
          phone: ""
        });

        // Product database loaded from script tag
        const allProducts = ref([]);
        const recommendedProducts = ref([]);

        // Config variables
        const googleScriptUrl = window.QUIZ_V2_CONFIG?.googleScriptUrl || "";

        // Computed properties
        const currentQuestion = computed(() => {
          return window.gamePlay.find(q => q.id === currentQuestionId.value) || null;
        });

        const totalSteps = computed(() => {
          // Dynamic calculation of step based on path (estimated max 6)
          return 5;
        });

        const currentStep = computed(() => {
          return history.value.length + 1;
        });

        const progressPercentage = computed(() => {
          if (currentScreen.value === "lead_capture") return 90;
          if (currentScreen.value === "recommendations") return 100;
          return Math.min(Math.round(((currentStep.value - 1) / totalSteps.value) * 100), 90);
        });

        // Initialize Products
        onMounted(() => {
          const productsJsonEl = document.getElementById("quiz-v2-products-data");
          if (productsJsonEl) {
            try {
              allProducts.value = JSON.parse(productsJsonEl.textContent);
            } catch (err) {
              console.error("Failed to parse quiz products payload:", err);
            }
          }

          // Keyboard Accessibility
          window.addEventListener("keydown", handleKeyDown);
        });

        function handleKeyDown(e) {
          if (currentScreen.value !== "quiz") return;

          // Back on Left arrow
          if (e.key === "ArrowLeft" && history.value.length > 0) {
            goToPrevious();
          }

          // Continue on Right arrow / Enter if answer is selected
          if ((e.key === "ArrowRight" || e.key === "Enter") && selectedAnswer.value) {
            goToNext();
          }

          // Select options with number keys (1, 2, 3, etc.)
          if (currentQuestion.value && /^[1-9]$/.test(e.key)) {
            const index = parseInt(e.key) - 1;
            const answersList = currentQuestion.value.Answers;
            if (answersList && answersList[index]) {
              selectAndProceed(answersList[index]);
            }
          }
        }

        // Navigation Methods
        function selectAndProceed(answer) {
          selectedAnswer.value = answer;
          // Soft feedback delay before moving to next screen
          setTimeout(() => {
            goToNext();
          }, 600);
        }

        function goToNext() {
          if (!selectedAnswer.value || !currentQuestion.value) return;

          const answer = selectedAnswer.value;
          const question = currentQuestion.value;

          // Record step details into history stack for back nav
          history.value.push({
            questionId: currentQuestionId.value,
            selectedAnswer: answer,
            answersSnapshot: { ...answers.value }
          });

          // Map values to Flat Answers object
          mapAnswerToState(question, answer);

          // Check if this answer reaches the end branch (goTo is a product identifier string)
          if (typeof answer.goTo === "string") {
            // Save final gameplay style
            answers.value.gameplay = answer.response;
            
            // Generate product matches
            calculateRecommendations();

            // Navigate to lead capture
            currentScreen.value = "lead_capture";
            selectedAnswer.value = null;
          } else {
            // Proceed to the next branching question ID
            currentQuestionId.value = answer.goTo;
            selectedAnswer.value = null;
          }
        }

        function goToPrevious() {
          if (history.value.length === 0) return;

          const lastState = history.value.pop();
          currentQuestionId.value = lastState.questionId;
          selectedAnswer.value = lastState.selectedAnswer;
          answers.value = lastState.answersSnapshot;
        }

        function mapAnswerToState(question, answer) {
          const qText = (question.Question || "").toLowerCase();
          const key = question.key;

          // 1. Sport
          if (qText.includes("which sport") || question.id === 1) {
            answers.value.sport = answer.response;
          }
          // 2. Level / Skill
          else if (key === "Skill") {
            answers.value.level = answer.response;
          }
          // 3. Goal / Achievement
          else if (key === "Achievement" || qText.includes("motivating") || qText.includes("achieve")) {
            answers.value.goal = answer.response;
          }
          // 4. Gameplay style
          else if (key === "Strength") {
            answers.value.gameplay = answer.response;
          }
          // 5. Experience (Yes/No, play frequency)
          else {
            answers.value.experience = answer.response;
          }
        }

        // Dynamic Recommendation engine logic
        function calculateRecommendations() {
          const sportSelection = answers.value.sport;
          const levelSelection = answers.value.level;
          const gameplaySelection = answers.value.gameplay;

          // Formulate matching tag arrays (accounting for synonym variants)
          let sportTags = [];
          if (sportSelection === "Padel") {
            sportTags = ["Padel Tennis", "Padel"];
          } else if (sportSelection === "Table Tennis") {
            sportTags = ["Table Tennis", "Table-Tennis"];
          } else {
            sportTags = [sportSelection];
          }

          let levelTags = [];
          if (levelSelection === "Advanced") {
            levelTags = ["Advanced", "Advance"];
          } else {
            levelTags = [levelSelection];
          }

          let gameplayTags = [gameplaySelection];

          // Filter product collection dynamically
          let matches = allProducts.value.filter(product => {
            const prodTags = (product.tags || []).map(t => t.toLowerCase());

            const matchSport = sportTags.some(tag => prodTags.includes(tag.toLowerCase()));
            const matchLevel = levelTags.some(tag => prodTags.includes(tag.toLowerCase()));
            const matchGameplay = gameplayTags.some(tag => prodTags.includes(tag.toLowerCase()));

            return matchSport && matchLevel && matchGameplay;
          });

          // Fallback strategy: if no product matches all three, drop Level filter, then gameplay filter
          if (matches.length === 0) {
            matches = allProducts.value.filter(product => {
              const prodTags = (product.tags || []).map(t => t.toLowerCase());
              const matchSport = sportTags.some(tag => prodTags.includes(tag.toLowerCase()));
              const matchGameplay = gameplayTags.some(tag => prodTags.includes(tag.toLowerCase()));
              return matchSport && matchGameplay;
            });
          }

          if (matches.length === 0) {
            matches = allProducts.value.filter(product => {
              const prodTags = (product.tags || []).map(t => t.toLowerCase());
              const matchSport = sportTags.some(tag => prodTags.includes(tag.toLowerCase()));
              return matchSport;
            });
          }

          recommendedProducts.value = matches;
        }

        // Form validation
        function validateForm() {
          let isValid = true;
          errors.value.name = "";
          errors.value.email = "";
          errors.value.phone = "";

          if (leadForm.value.name.trim().length < 2) {
            errors.value.name = "Name must be at least 2 characters.";
            isValid = false;
          }

          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(leadForm.value.email.trim())) {
            errors.value.email = "Please enter a valid email address.";
            isValid = false;
          }

          const phoneDigits = leadForm.value.phone.replace(/\D/g, "");
          if (phoneDigits.length < 10) {
            errors.value.phone = "Please enter a valid 10-digit phone number.";
            isValid = false;
          }

          return isValid;
        }

        // Submit leads to Google Sheets and Shopify Contact form fallback
        async function submitLead() {
          if (!validateForm()) return;

          isSubmitting.value = true;
          const errorContainer = document.getElementById("quiz-v2-general-err");
          if (errorContainer) errorContainer.style.display = "none";

          const payload = {
            name: leadForm.value.name.trim(),
            email: leadForm.value.email.trim(),
            phone: "'" + leadForm.value.countryCode + " " + leadForm.value.phone.replace(/\D/g, "")
          };

          let sheetSuccess = false;

          // 1. Submit to Google Sheets (Web App)
          if (googleScriptUrl) {
            try {
              // POST with no-cors to handle Google Script redirects gracefully
              await fetch(googleScriptUrl, {
                method: "POST",
                mode: "no-cors",
                headers: {
                  "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
              });
              sheetSuccess = true;
            } catch (err) {
              console.warn("Failed submitting lead to Google Apps Script:", err);
            }
          } else {
            console.warn("Google Apps Script URL is empty. Skipping sheet submit.");
            sheetSuccess = true; // allow bypass if url is empty
          }

          // 2. Submit to Shopify contact database as backup
          try {
            const formData = new FormData();
            formData.append("form_type", "contact");
            formData.append("utf8", "✓");
            formData.append("contact[name]", payload.name);
            formData.append("contact[email]", payload.email);
            formData.append("contact[phone]", payload.phone);
            formData.append(
              "contact[body]",
              `Quiz V2 Lead\nSport: ${answers.value.sport}\nLevel: ${answers.value.level}\nGameplay: ${answers.value.gameplay}`
            );

            await fetch("/contact#contact_form", {
              method: "POST",
              body: formData
            });
          } catch (err) {
            console.warn("Shopify contact fallback submit error:", err);
          }

          isSubmitting.value = false;

          if (sheetSuccess) {
            currentScreen.value = "recommendations";
          } else {
            if (errorContainer) {
              errorContainer.textContent = "Submission failed. Please check your connection and try again.";
              errorContainer.style.display = "block";
            }
          }
        }

        // AJAX Add to Cart integration with Minimog
        async function addToCart(variantId, event) {
          if (!variantId) return;

          addingToCartId.value = variantId;

          try {
            const response = await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json"
              },
              body: JSON.stringify({
                id: variantId,
                quantity: 1
              })
            });

            const item = await response.json();
            
            // Fetch updated cart representation
            const cartRes = await fetch("/cart.js");
            const cart = await cartRes.json();

            // Emit Minimog Events to refresh theme components
            if (window.MinimogEvents && window.MinimogTheme) {
              window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
                cart: cart,
                productVariantId: variantId
              });
              window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.openCartDrawer);
            } else {
              // Standard fallback
              window.location.href = "/cart";
            }
          } catch (error) {
            console.error("Cart AJAX error:", error);
            alert("Could not add item to cart. Please try again.");
          } finally {
            addingToCartId.value = null;
          }
        }

        // Price formatting helpers
        function calculateDiscount(price, comparePrice) {
          if (!comparePrice || comparePrice <= price) return 0;
          return Math.round(((comparePrice - price) / comparePrice) * 100);
        }

        function restartQuiz() {
          currentScreen.value = "quiz";
          currentQuestionId.value = 1;
          selectedAnswer.value = null;
          history.value = [];
          leadForm.value = { name: "", email: "", phone: "", countryCode: "+91" };
          answers.value = { sport: "", experience: "", level: "", goal: "", gameplay: "" };
          recommendedProducts.value = [];
        }

        return {
          currentScreen,
          currentQuestion,
          selectedAnswer,
          history,
          isSubmitting,
          addingToCartId,
          answers,
          leadForm,
          errors,
          recommendedProducts,
          totalSteps,
          currentStep,
          progressPercentage,
          selectAndProceed,
          goToNext,
          goToPrevious,
          submitLead,
          addToCart,
          calculateDiscount,
          restartQuiz
        };
      }
    }).mount("#quizAppV2");
  }
});
